/*******************************************************************************
* Licensed Materials - Property of HCL
* (c) Copyright HCL Technologies Ltd. 2020, 2023. All Rights Reserved.

* Note to U.S. Government Users Restricted Rights:
* Use, duplication or disclosure restricted by GSA ADP Schedule
**************************************************************/

import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Panel,
    Text,
    DropdownCheckboxes,
    TopologyAdvanced,
    Spinner,
    SelectionButton,
    Button,
} from "vr-react-components";
import Tooltip from "../../widgets/TopologyWidget/TopologyWidgetTooltip";
import { nodeGenerator } from "../../widgets/TopologyWidget/Utils";
import {
    handleSetChecked,
    handleSetPosition,
    handleSetZoom,
    linkDirectionalParticleWidth
} from "../../handlers/TopologyRoomHandlers";
import { JOB_ROOM, TOPOLOGY_ROOM } from "../RoomNameConstants";
import { ObjectClone } from "../../utils/ObjectUtils";
import { setLockControl } from "../../store/slice/privateSlice";
import store from "../../store/store";
import { getTopologyRoomData } from "../../multiplayer/SocketConnection";
import { setPollingDelay, setTopologyGraphData } from "../../store/slice/roomSlices/topologyRoomSlice";

const Center = React.memo(({ width, height, depth }) => {
    const pollingDelay = useSelector((state) => state[TOPOLOGY_ROOM].graph.pollingDelay);
    const selectedJob = useSelector((state) => state[JOB_ROOM].selectedJob);
    const data = useSelector((state) => state[TOPOLOGY_ROOM].graph);
    const device = useSelector((state) => state.private.device);

    useEffect(() => {
        let interval
        if (interval) clearInterval(interval)

        if (pollingDelay !== 'none')
            interval = setInterval(() => {
                getTopologyRoomData({
                    selectedJob,
                    targetRoom: TOPOLOGY_ROOM
                })
            }, pollingDelay * 1000)

        return () => {
            clearInterval(interval)
        }
    }, [pollingDelay, selectedJob])

    if (!data.data) return null;
    return (
        <TopologyAdvanced
            width={width}
            height={height}
            depth={depth}
            setZoom={handleSetZoom}
            setPosition={handleSetPosition}
            setLockControls={(lockStatus) => {
                store.dispatch(setLockControl(lockStatus))
            }}
            data={ObjectClone(data)}
            deviceData={device}
            nodeGenerator={nodeGenerator}
            linkDirectionalParticleWidth={linkDirectionalParticleWidth}
        />
    );
});

const Loader = () => {
    const isLoading = useSelector(
        (state) => !state[TOPOLOGY_ROOM].graph  ,
    );

    const [rendering, setRendering] = useState(true)

    useEffect(() => {
        const timeout = setTimeout(() => {
            setRendering(false)
        }, 1500)

        return () => {
            clearTimeout(timeout)
        }
    })

    return isLoading || rendering ? <Spinner radius={0.5} /> : null;
};

const Right = React.memo(({ width, height }) => {
    const currentElement = useSelector(
        (state) => state[TOPOLOGY_ROOM].graph.currentHoverElement,
    );
    const isLoading = useSelector(
        (state) => !state[TOPOLOGY_ROOM].graph || state.private.isLoading > 0,
    );
    if (isLoading || !currentElement) return null;

    return <Tooltip width={width} height={height} element={currentElement} />;
});

const PollingPanel = ({ width, height, aframeClasses }) => {
    const dispatch = useDispatch()

    const pollingDelay = useSelector((state) => state[TOPOLOGY_ROOM].graph.pollingDelay);
    const canModify = useSelector((state) => state.private.device.canModify);
    const selectedJob = useSelector((state) => state[JOB_ROOM].selectedJob);

    const pollingDelays = ['5', '10', '15', '30', '60', '180', 'none'];

    const [checked, setChecked] = useState(pollingDelay);

    return (
        <Panel width={width} height={height} panel>
            <a-entity
                position={`${-width / 2 + width * 0.05} ${height / 2 - height / 10} 0.01`}
            >
                <Text
                    text="Select polling delay"
                    width={width * 0.9}
                    align="left"
                    sizeDecimal={30 / width}
                    baseline="top"
                />

                <Button
                    text={"Refresh Now"}
                    type={"primary"}
                    disabled={pollingDelay !== "none"}
                    aframeClasses={canModify ? "collidable" : ""}
                    position={`${width * 0.45 + width * 0.25} -0.04 ${width / 200
                        }`}
                    width={width * 0.35}
                    height={height * 0.23}
                    textSizeDecimal={40 / width}
                    onClickHandler={() => {
                        dispatch(setTopologyGraphData({ nodes: [], links: [] }))
                        getTopologyRoomData({
                            selectedJob,
                            targetRoom: TOPOLOGY_ROOM
                        })
                    }}
                />

                <Text
                    text="Unselect to doesn't receive intime update"
                    width={width * 0.9}
                    align="left"
                    sizeDecimal={40 / width}
                    position={`0 ${-height / 5 - (height / 10) * 2} 0`}
                />

                <SelectionButton
                    id={pollingDelays[0]}
                    width={(width * 0.8) / 2}
                    height={height / 5}
                    choices={pollingDelays}
                    current={checked}
                    setCurrent={(el) => {
                        setChecked(el)
                        dispatch(setPollingDelay(el));
                    }}
                    aframeClasses={aframeClasses}
                    position={`${(width * 1.5) / 4} ${-height / 3.5 - (height / 10) * 4
                        } 0`}
                    textSizeDecimal={40}
                />
            </a-entity>
        </Panel>
    )
}

const Left = ({ width, height }) => {
    const filterStatus = useSelector((state) => state[TOPOLOGY_ROOM].graph.filter);
    const canModify = useSelector((state) => state.private.device.canModify);

    const choices = ["Error", "Waiting", "Successful", "Running"];

    return (
        <Panel width={width} height={height} panel>
            <a-entity
                position={`${-width / 2 + width * 0.05} ${height / 2 - height / 10} 0.01`}
            >
                <Text
                    text="Select Jobs by status"
                    width={width * 0.9}
                    align="left"
                    sizeDecimal={30 / width}
                    baseline="top"
                />

                <Text
                    text="Unselect to hide jobs with specific status"
                    width={width * 0.9}
                    align="left"
                    sizeDecimal={40 / width}
                    position={`0 ${-height / 5 - (height / 10) * 2} 0`}
                />

                <DropdownCheckboxes
                    id={choices[0]}
                    width={width * 0.8}
                    height={height / 5}
                    position={`${width / 2 - width * 0.05} ${-height / 5 - (height / 10) * 4
                        } 0`}
                    textSizeDecimal={40 / width}
                    text="Select"
                    choices={choices}
                    checked={choices.map((e, i) => {
                        return filterStatus.includes(e);
                    })}
                    setChecked={(handleSetChecked(choices))}
                    aframeClasses={canModify ? "collidable" : ""}
                />
            </a-entity>
        </Panel>
    );
};

const TopologyRoom = (props) => {
    const device = useSelector((state) => state.private.device)

    const isVROrMobile = device.isMobile || device.areControllersConnected;

    return (
        <a-entity class="Topology Room">
            <a-entity position="0 0.4 -6">
                <Center width="8" height="4.5" depth="5" />
            </a-entity>
            <a-entity position="0 1.5 -5.5">
                <Loader />
            </a-entity>
            <a-entity position="-1.7 2.3 -1.8" rotation={`0 ${isVROrMobile ? 45 : 0} 0`}>
                <Left width={0.9} height={0.4} />
            </a-entity>
            <a-entity position="1.7 1.2 -1.5" rotation={`0 ${isVROrMobile ? -45 : 0} 0`}>
                <Right width={0.9} height={0.4} />
            </a-entity>
            <a-entity position="1.5 2.1 -1.5" rotation={`0 ${isVROrMobile ? -45 : 0} 0`}>
                <PollingPanel width={0.9} height={0.4} aframeClasses={"collidable"} />
            </a-entity>
        </a-entity>
    );
};

export default TopologyRoom;
